import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, FormGroup } from '@angular/forms';

/**
 * Puts a server refusal where the user is looking. Each message the server names
 * a field for goes on that field as `serverError`, for its `<mat-error>`, and
 * clears when the field is next edited. Returns whatever is left for `[error]`
 * on `<app-modal>`: the refusal of the act as a whole, or null if every message
 * found its field.
 *
 * `fieldMap` names the controls that aren't called what the API calls them.
 */
export function placeRefusal(
  form: FormGroup,
  err: unknown,
  fallback: string,
  fieldMap: Readonly<Record<string, string>> = {},
): string | null {
  const detail: unknown = err instanceof HttpErrorResponse ? err.error?.detail : null;
  if (typeof detail === 'string' && detail) return detail;
  if (!detail || typeof detail !== 'object') return fallback;

  const left: string[] = [];
  for (const [field, message] of Object.entries(detail as Record<string, string | string[]>)) {
    const text = Array.isArray(message) ? message[0] : message;
    const control = form.get(fieldMap[field] ?? field);
    if (control) {
      control.setErrors({ serverError: text });
      control.markAsTouched();
    } else {
      left.push(text);
    }
  }
  return left.length ? left.join(' ') : null;
}

/**
 * Puts each message on the control it is about, so it shows in that field's
 * `<mat-error>` and clears when the field is next edited. For a required fact
 * that depends on another answer, where a fixed validator can't say. Returns
 * true if anything was missing.
 */
export function flagMissing(
  checks: readonly (readonly [AbstractControl, string | null])[],
): boolean {
  let missing = false;
  for (const [control, message] of checks) {
    if (!message) continue;
    control.setErrors({ missing: message });
    control.markAsTouched();
    missing = true;
  }
  return missing;
}

/** What to show under a field: `flagMissing`'s message or the server's refusal. */
export function fieldError(control: AbstractControl): string | null {
  return control.getError('missing') ?? control.getError('serverError') ?? null;
}
